import React, { useState, useEffect } from "react";
import "../styles/userProfile.css";
import { useAuth } from "../context/AuthContext";
import { addPost, getUserPosts, deletePost, getUserSettings } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineDelete,
  AiOutlineCamera,
  AiOutlineEnvironment,
  AiOutlineCloseCircle,
} from "react-icons/ai";
import { FaUserCircle } from "react-icons/fa";

const AREAS = ["Tiruchengode", "Sankagiri", "Kumarapalayam", "Pallipalayam"];

async function uploadToImgBB(file) {
  if (!file) return "";
  const key = process.env.REACT_APP_IMGBB_KEY || "YOUR_IMGBB_API_KEY";
  const fd = new FormData();
  fd.append("image", file);
  const res = await fetch(`https://api.imgbb.com/1/upload?key=${key}`, {
    method: "POST",
    body: fd,
  });
  const data = await res.json();
  return data?.data?.url || "";
}

export default function UserPosts() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [userArea, setUserArea] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);

  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [pendingPost, setPendingPost] = useState(false); // <-- track when user is posting without location

  // Load user settings & posts
  const load = async () => {
    const list = await getUserPosts(user.uid);
    setPosts(list);
  };

  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      try {
        const s = await getUserSettings(user.uid);
        if (s) {
          setUserArea(s.area || "");
          setProfileName(s.name || user.displayName || "User");
          setProfilePhoto(s.photoURL || user.photoURL || null);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setProfileName(user.displayName || "User");
        setProfilePhoto(user.photoURL || null);
      }
      await load();
    })();
  }, [user]);

  // Create Post (Home-style)
  const handlePost = async () => {
    if (!text.trim() && !image) {
      alert("Write something or add an image!");
      return;
    }

    const areaToUse = selectedArea || userArea;

    // ✅ Require location selection before posting
    if (!areaToUse) {
      setPendingPost(true); // mark that a post attempt is pending
      setShowLocationPicker(true);
      return;
    }

    await actuallyPost(areaToUse);
  };

  // ✅ Core post upload logic
  const actuallyPost = async (areaToUse) => {
    try {
      const url = await uploadToImgBB(image);
      await addPost(user.uid, text, url, areaToUse, profileName, profilePhoto);
      setText("");
      setImage(null);
      setPreview(null);
      setSelectedArea("");
      setShowLocationPicker(false);
      setPendingPost(false);
      await load();
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed");
    }
  };

  // Delete post
  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await deletePost(postId);
    await load();
  };

  // Handle image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const removePreview = () => {
    setImage(null);
    setPreview(null);
  };

  // ✅ Handle location confirmation
  const confirmLocation = async () => {
    if (!selectedArea) {
      alert("Please select a location before posting.");
      return;
    }

    setShowLocationPicker(false);
    if (pendingPost) {
      // continue post after selecting
      await actuallyPost(selectedArea);
    }
  };

  return (
    <div className="profile-page">
      {/* ===== Header ===== */}
      <button className="back-btn" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <h2 style={{ fontSize: "22px", marginBottom: "18px", fontWeight: "700" }}>
        My Posts
      </h2>

      {/* ===== Composer (same as Home) ===== */}
      <div className="composer">
        {profilePhoto ? (
          <img
            src={profilePhoto}
            alt="User"
            className="composer-avatar"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <FaUserCircle className="composer-avatar-icon" />
        )}

        <input
          className="composer-input"
          placeholder="Post something..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <label className="composer-attach">
          <input hidden type="file" accept="image/*" onChange={handleImageChange} />
          <AiOutlineCamera size={20} />
        </label>

        <div
          className="composer-location"
          onClick={() => setShowLocationPicker(true)}
          style={{ cursor: "pointer" }}
        >
          <AiOutlineEnvironment size={20} />
        </div>

        <button className="composer-post" onClick={handlePost}>
          Post
        </button>
      </div>

      {/* ✅ Image Preview */}
      {preview && (
        <div className="image-preview-box">
          <img src={preview} alt="Preview" className="image-preview" />
          <button className="remove-preview-btn" onClick={removePreview}>
            <AiOutlineCloseCircle size={22} />
          </button>
        </div>
      )}

      {/* 📍 Location Picker Modal (identical to Home) */}
      {showLocationPicker && (
        <div className="location-modal-overlay">
          <div className="location-modal">
            <h4>Select Location</h4>
            {AREAS.map((a) => (
              <label key={a}>
                <input
                  type="radio"
                  value={a}
                  checked={selectedArea === a}
                  onChange={(e) => setSelectedArea(e.target.value)}
                />
                {a}
              </label>
            ))}
            <button className="location-confirm-btn" onClick={confirmLocation}>
              Confirm
            </button>
          </div>
        </div>
      )}

      {/* ===== User Posts ===== */}
      <div className="posts-list" style={{ marginTop: "25px" }}>
        {posts.length === 0 ? (
          <p style={{ color: "#6b7280", marginTop: "20px" }}>No posts yet.</p>
        ) : (
          posts.map((p) => (
            <div key={p.id} className="post-item">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "8px",
                }}
              >
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Profile"
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <FaUserCircle style={{ fontSize: "36px", color: "#ccc" }} />
                )}
                <span
                  style={{ fontWeight: 600, color: "#222", fontSize: "15px" }}
                >
                  {profileName}
                </span>
              </div>

              {p.area && <span className="post-area">📍 {p.area}</span>}
              {p.text && <p className="post-text">{p.text}</p>}
              {p.imageUrl && <img src={p.imageUrl} alt="" className="post-img" />}

              <button
                className="delete-btn"
                onClick={() => handleDelete(p.id)}
                title="Delete Post"
              >
                <AiOutlineDelete size={18} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
